import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Dialog, TextField } from '@mui/material';
import { X } from 'lucide-react';

import Text from 'components/text';
import Checkbox from 'components/checkbox';
import api from 'utils/api';
import { useCollabClubArtists } from 'hooks/swrRequests/useCollabClubArtists';
import { CcMatchPreview } from 'hooks/swrRequests/useAdminCcRequests';
import type { Artist } from 'types';

import { FONT } from './constants';
import FieldLabel from './components/field-label';
import ArtistPicker from './components/artist-picker';
import ArtistProfileCard, { ArtistProfileCardHandle } from './components/artist-profile-card';
import PrimaryButton from './components/primary-button';
import SecondaryButton from 'components/admin/secondary-button';
import ConfirmLogRequestModal from './confirm-log-request-modal';

interface LogMatchModalProps {
  open: boolean;
  onClose: () => void;
  onCreated: () => void;
}

const todayISO = (): string => new Date().toISOString().slice(0, 10);

const LogMatchModal: React.FC<LogMatchModalProps> = ({ open, onClose, onCreated }) => {
  const { artists, refetch: refetchArtists } = useCollabClubArtists();

  const [artistA, setArtistA] = useState<Artist | null>(null);
  const [artistB, setArtistB] = useState<Artist | null>(null);
  const [date, setDate] = useState<string>(todayISO());
  const [alreadyMatched, setAlreadyMatched] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Confirmation step: previews the would-be match before committing it.
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [reviewing, setReviewing] = useState(false);
  const [preview, setPreview] = useState<CcMatchPreview | null>(null);

  // Auto-save each artist's profile edits as part of logging the match.
  const cardARef = useRef<ArtistProfileCardHandle>(null);
  const cardBRef = useRef<ArtistProfileCardHandle>(null);

  // Reset the form each time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setArtistA(null);
    setArtistB(null);
    setDate(todayISO());
    setAlreadyMatched(false);
    setConfirmOpen(false);
    setReviewing(false);
    setPreview(null);
    setSubmitting(false);
    setErrorMsg(null);
  }, [open]);

  const sameArtist = !!artistA && !!artistB && artistA.id === artistB.id;
  const bothPicked = !!artistA && !!artistB && !sameArtist;

  const optionsA = useMemo(() => artists.filter((a) => a.id !== artistB?.id), [artists, artistB]);
  const optionsB = useMemo(() => artists.filter((a) => a.id !== artistA?.id), [artists, artistA]);

  const canReview = bothPicked && !reviewing && !submitting;

  // After a profile save, refetch the roster and refresh the selected artists so their
  // ArtistIdentity (name / meta line) reflects the new details.
  const handleProfileSaved = async () => {
    const fresh = await refetchArtists();
    if (!fresh) return;
    setArtistA((cur) => (cur ? fresh.find((a) => a.id === cur.id) ?? cur : cur));
    setArtistB((cur) => (cur ? fresh.find((a) => a.id === cur.id) ?? cur : cur));
  };

  // Step 1 — preview the would-be match, then open the confirmation dialog.
  const handleReview = async () => {
    if (!artistA || !artistB || !bothPicked) return;
    setReviewing(true);
    setErrorMsg(null);
    try {
      const result: CcMatchPreview = await api.collabClub.previewAdminMatch({
        data: { ccArtistId1: artistA.id, ccArtistId2: artistB.id },
      });
      setPreview(result ?? null);
      setConfirmOpen(true);
    } catch (err: any) {
      setErrorMsg(
        err?.data?.message || err?.message || 'Failed to preview the match. Please try again.'
      );
    } finally {
      setReviewing(false);
    }
  };

  // Step 2 — commit the match: from the confirmation dialog, or directly (no
  // preview/confirmation) when the pair is flagged as already matched.
  const handleSubmit = async () => {
    if (!artistA || !artistB || !bothPicked) return;
    setSubmitting(true);
    setErrorMsg(null);
    try {
      // Persist any profile edits first so they're not lost; a failure aborts the match.
      await Promise.all([cardARef.current?.save(), cardBRef.current?.save()]);
      await api.collabClub.createAdminMatch({
        data: {
          ccArtistId1: artistA.id,
          ccArtistId2: artistB.id,
          matchDate: date || undefined,
          skipIntroEmails: alreadyMatched,
        },
      });
      setConfirmOpen(false);
      onCreated();
    } catch (err: any) {
      setConfirmOpen(false);
      setErrorMsg(
        err?.data?.message || err?.message || 'Failed to log the match. Please try again.'
      );
      setSubmitting(false);
    }
  };

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '12px',
            '& .MuiInputBase-input': { fontFamily: 'inherit' },
            '& .MuiAvatar-root': { fontFamily: 'inherit' },
          },
        }}
      >
        {/* Header */}
        <Box
          display="flex"
          alignItems="flex-start"
          justifyContent="space-between"
          padding="22px 24px 18px"
        >
          <Text fontSize={FONT.lg} fontWeight={700}>
            Log a match manually
          </Text>
          <Box
            component="button"
            onClick={onClose}
            aria-label="Close"
            display="flex"
            sx={{
              cursor: 'pointer',
              border: 'none',
              background: 'none',
              padding: 0,
              color: '#64748b',
            }}
          >
            <X size={20} />
          </Box>
        </Box>

        {/* Body */}
        <Box
          padding="0 24px 8px"
          borderTop="1px solid #e2e8f0"
          paddingTop="20px"
          display="flex"
          flexDirection="column"
          gap={2.5}
          sx={{ maxHeight: '64vh', overflowY: 'auto' }}
        >
          {/* Pair selectors */}
          <Box>
            <FieldLabel required>Artists</FieldLabel>
            <Box display="flex" gap={1.5} alignItems="center" flexWrap="wrap">
              <Box flex="1 1 200px" minWidth={180}>
                <ArtistPicker
                  options={optionsA}
                  value={artistA}
                  onChange={setArtistA}
                  placeholder="Select artist A…"
                />
              </Box>
              <Box component="span" color="#94a3b8" fontSize={FONT.lg}>
                ×
              </Box>
              <Box flex="1 1 200px" minWidth={180}>
                <ArtistPicker
                  options={optionsB}
                  value={artistB}
                  onChange={setArtistB}
                  placeholder="Select artist B…"
                />
              </Box>
            </Box>
            {sameArtist && (
              <Text fontSize={FONT.sm} color="#dc2626" marginTop="8px">
                Pick two different artists.
              </Text>
            )}
          </Box>

          {/* Per-artist profile cards */}
          {(artistA || artistB) && (
            <Box>
              <FieldLabel>Profiles</FieldLabel>
              <Box display="flex" flexDirection="column" gap={2}>
                {artistA && (
                  <ArtistProfileCard
                    key={artistA.id}
                    ref={cardARef}
                    artist={artistA}
                    onSaved={handleProfileSaved}
                  />
                )}
                {artistB && (
                  <ArtistProfileCard
                    key={artistB.id}
                    ref={cardBRef}
                    artist={artistB}
                    onSaved={handleProfileSaved}
                  />
                )}
              </Box>
            </Box>
          )}

          {/* Date */}
          <Box>
            <FieldLabel>Date</FieldLabel>
            <TextField
              type="date"
              size="small"
              value={date}
              onChange={(e) => setDate(e.target.value)}
              sx={{ maxWidth: 240 }}
            />
          </Box>

          {/* Already matched */}
          <Box
            component="label"
            display="flex"
            alignItems="flex-start"
            gap={1}
            sx={{ cursor: 'pointer' }}
          >
            <Checkbox
              checked={alreadyMatched}
              onChange={(e) => setAlreadyMatched(e.target.checked)}
              sx={{ padding: 0 }}
            />
            <Box display="flex" flexDirection="column" gap="2px">
              <Text fontSize={FONT.base} fontWeight={600}>
                Already matched
              </Text>
              <Text fontSize={FONT.sm} color="#64748b">
                These artists are already collaborating — just record it. No intro emails will be
                sent.
              </Text>
            </Box>
          </Box>

          {errorMsg && (
            <Text fontSize={FONT.sm} color="#dc2626">
              {errorMsg}
            </Text>
          )}
        </Box>

        {/* Footer */}
        <Box
          display="flex"
          alignItems="center"
          justifyContent="flex-end"
          gap={1}
          padding="16px 24px"
          borderTop="1px solid #e2e8f0"
          marginTop="12px"
        >
          <SecondaryButton variant="text" onClick={onClose} disabled={submitting}>
            Cancel
          </SecondaryButton>
          <PrimaryButton
            variant="contained"
            onClick={alreadyMatched ? handleSubmit : handleReview}
            disabled={!canReview}
            startIcon={
              reviewing || submitting ? <CircularProgress size={16} color="inherit" /> : null
            }
          >
            {alreadyMatched ? 'Log match' : 'Review'}
          </PrimaryButton>
        </Box>
      </Dialog>

      <ConfirmLogRequestModal
        open={confirmOpen}
        preview={preview ? [preview] : null}
        fromArtist={null}
        wants={[]}
        submitting={submitting}
        onClose={() => setConfirmOpen(false)}
        onConfirm={handleSubmit}
      />
    </>
  );
};

export default LogMatchModal;
